import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { ItemChangeRule, ItemChangeFillMapping } from '../txn/types'
import { type DesignerCatalog, errorMessage, isNotFound } from './catalog'
import { modalOf } from './KitModal'

interface ItemChangeRuleEditorProps {
  open: boolean
  onClose: () => void
  value: ItemChangeRule | null
  triggerColumnName: string
  /** Form-level event (row_add / row_edit) rather than a per-column
   *  item_change. There is no trigger column: the simple "this column = …"
   *  match is hidden (use an advanced WHERE clause), every form column is a
   *  valid fill target, and the trigger-loop warning is suppressed. Defaults
   *  to true when ``triggerColumnName`` is empty. */
  formLevel?: boolean
  formColumnNames: string[]
  /** Parent/header-form column names, offered as {header.X} tokens in the
   *  expression builder. Empty when editing the header form itself. */
  headerColumnNames?: string[]
  /** Project folder name (the host's catalog is bound to it). */
  project: string
  onSave: (rule: ItemChangeRule) => void
  /** Where tables and columns come from (designerKit/catalog). Absent ⇒ the
   *  editor degrades to plain text inputs. */
  catalog?: DesignerCatalog
}

// A fill row is an expression fill when it carries an `expr` key (even
// empty) and no table column; otherwise it's a table-column lookup fill.
function isExprFill(f: ItemChangeFillMapping): boolean {
  return f.expr !== undefined && !(f.table_col || '').trim()
}

interface TableSummary { name: string; description?: string }
interface ColumnSummary { column_name: string; data_type?: string }

function cmpNoCase(a: string, b: string) {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9125rem', minHeight: 36, boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 12,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem',
  fontWeight: 600,
  color: 'var(--color-accent)',
  marginTop: 14,
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid var(--color-border)',
}

const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginTop: 4,
  fontStyle: 'italic',
}

const errorStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: '#ef4444',
  marginTop: 4,
  fontWeight: 500,
}

const advancedLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-accent)',
  cursor: 'pointer',
  fontSize: '0.8125rem',
  padding: 0,
  marginTop: 4,
  textDecoration: 'underline',
}

const tokenChipStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  padding: '1px 6px',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  cursor: 'pointer',
}

const addBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  padding: '4px 8px', fontSize: '0.8125rem',
  color: 'var(--color-accent)', background: 'none',
  border: '1px dashed var(--color-border)', borderRadius: 4,
  cursor: 'pointer', flex: 1,
}

function kindBtn(active: boolean): React.CSSProperties {
  return {
    padding: '4px 8px',
    fontSize: '0.8125rem',
    border: 'none',
    background: active ? 'var(--color-accent)' : 'var(--color-bg)',
    color: active ? '#fff' : 'var(--color-text)',
    cursor: 'pointer',
  }
}

function emptyRule(): ItemChangeRule {
  return {
    mode: 'rule',
    source_table: '',
    match_mode: 'simple',
    match_table_col: '',
    where_sql: '',
    fill: [],
    on_no_match: undefined,
    on_multi_match: undefined,
  }
}

export default function ItemChangeRuleEditor({
  open,
  onClose,
  value,
  triggerColumnName,
  formLevel: formLevelProp,
  formColumnNames,
  headerColumnNames = [],
  project,
  onSave,
  catalog,
}: ItemChangeRuleEditorProps) {
  const Modal = modalOf(catalog)
  // Form-level (row_add / row_edit) when explicitly flagged, or whenever no
  // trigger column is supplied — there is then nothing to drive a simple match.
  const formLevel = formLevelProp ?? !triggerColumnName
  const [draft, setDraft] = useState<ItemChangeRule>(emptyRule())
  const [showAdvancedMatch, setShowAdvancedMatch] = useState(false)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const tablesListId = useId()
  const columnsListId = useId()
  const [tables, setTables] = useState<TableSummary[]>([])
  const [tablesError, setTablesError] = useState<string | null>(null)
  // Source-table column cache so re-typing the same table doesn't refetch.
  const [columnsByTable, setColumnsByTable] = useState<Record<string, ColumnSummary[]>>({})
  const [columnsLoading, setColumnsLoading] = useState(false)
  const [columnsError, setColumnsError] = useState<string | null>(null)
  const columnsRequestSeq = useRef(0)

  // Fetch the list of project tables once when the modal opens.
  useEffect(() => {
    if (!open || !catalog) return
    let cancelled = false
    setTablesError(null)
    catalog.tables()
      .then((res) => {
        if (cancelled) return
        const list: TableSummary[] = Array.isArray(res) ? res : []
        setTables([...list].sort((a, b) => cmpNoCase(a.name, b.name)))
      })
      .catch((err) => {
        if (cancelled) return
        setTablesError(errorMessage(err, 'Failed to load project tables'))
      })
    return () => { cancelled = true }
  }, [open, project, catalog])

  useEffect(() => {
    if (!open) return
    const initial = value ? { ...emptyRule(), ...value, mode: 'rule' as const } : emptyRule()
    setDraft(initial)
    // Form-level events have no trigger value, so simple match can't run —
    // always edit lookup fills via the advanced WHERE clause.
    setShowAdvancedMatch(formLevel || initial.match_mode === 'advanced')
    setShowAdvancedOptions(
      Boolean(initial.on_no_match || initial.on_multi_match),
    )
  }, [open, value, formLevel])

  // Fetch columns for whatever source_table the user has typed, with light
  // debouncing so we don't pelt the API while they're still typing.
  useEffect(() => {
    if (!open || !catalog) return
    const tbl = (draft.source_table || '').trim()
    if (!tbl) { setColumnsError(null); return }
    const cacheKey = tbl.toLowerCase()
    if (columnsByTable[cacheKey]) { setColumnsError(null); return }
    setColumnsError(null)
    setColumnsLoading(true)
    const seq = ++columnsRequestSeq.current
    const timer = window.setTimeout(() => {
      catalog.columns(tbl)
        .then((res) => {
          if (seq !== columnsRequestSeq.current) return
          const cols: ColumnSummary[] = Array.isArray(res) ? res : []
          setColumnsByTable(prev => ({ ...prev, [cacheKey]: cols }))
          setColumnsLoading(false)
        })
        .catch((err) => {
          if (seq !== columnsRequestSeq.current) return
          // Unknown tables (free-typed) just yield no suggestions — not an error.
          if (isNotFound(err)) {
            setColumnsByTable(prev => ({ ...prev, [cacheKey]: [] }))
          } else {
            setColumnsError(errorMessage(err, 'Failed to load table columns'))
          }
          setColumnsLoading(false)
        })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [open, project, catalog, draft.source_table, columnsByTable])

  const sourceTableColumns: ColumnSummary[] = useMemo(() => {
    const key = (draft.source_table || '').trim().toLowerCase()
    return columnsByTable[key] || []
  }, [columnsByTable, draft.source_table])

  // A rule needs the source table / match only when it has at least one
  // table-column (lookup) fill. A pure-expression rule needs neither.
  const hasLookupFill = useMemo(
    () => (draft.fill || []).some(f => !isExprFill(f) && (f.table_col || '').trim()),
    [draft.fill],
  )

  const errors = useMemo(() => {
    const e: { source_table?: string; match?: string; fill?: string } = {}
    if (hasLookupFill) {
      if (!draft.source_table.trim()) {
        e.source_table = 'Source table is required when filling from a table column.'
      }
      if (showAdvancedMatch) {
        if (!(draft.where_sql || '').trim()) {
          e.match = 'WHERE clause is required.'
        }
      } else {
        if (!(draft.match_table_col || '').trim()) {
          e.match = 'Match column is required.'
        }
      }
    }
    if (!draft.fill || draft.fill.length === 0) {
      e.fill = 'Add at least one fill mapping.'
    } else if (draft.fill.some(f => !f.form_col)) {
      e.fill = 'Every fill row must pick a form column.'
    } else if (draft.fill.some(f => isExprFill(f) ? !(f.expr || '').trim() : !(f.table_col || '').trim())) {
      e.fill = 'Every fill row needs a table column or an expression.'
    }
    return e
  }, [draft, showAdvancedMatch, hasLookupFill])

  const hasErrors = Object.values(errors).some(Boolean)

  const update = <K extends keyof ItemChangeRule>(k: K, v: ItemChangeRule[K]) => {
    setDraft(prev => ({ ...prev, [k]: v }))
  }

  const updateFill = (i: number, patch: Partial<ItemChangeFillMapping>) => {
    setDraft(prev => {
      const fill = [...(prev.fill || [])]
      fill[i] = { ...fill[i], ...patch }
      return { ...prev, fill }
    })
  }

  const addFill = () => {
    setDraft(prev => ({
      ...prev,
      fill: [...(prev.fill || []), { form_col: '', table_col: '' }],
    }))
  }

  const addExprFill = () => {
    setDraft(prev => ({
      ...prev,
      fill: [...(prev.fill || []), { form_col: '', expr: '' }],
    }))
  }

  // Flip a row between table-column and expression kinds, preserving the
  // form column and any partially-typed value of the other kind.
  const setFillKind = (i: number, kind: 'table' | 'expr') => {
    setDraft(prev => {
      const fill = [...(prev.fill || [])]
      const cur = fill[i] || { form_col: '' }
      fill[i] = kind === 'expr'
        ? { form_col: cur.form_col, expr: cur.expr ?? '' }
        : { form_col: cur.form_col, table_col: cur.table_col ?? '' }
      return { ...prev, fill }
    })
  }

  // Append a {form.X} / {header.X} token to a row's expression.
  const appendToken = (i: number, token: string) => {
    setDraft(prev => {
      const fill = [...(prev.fill || [])]
      const cur = fill[i]
      if (!cur) return prev
      const existing = cur.expr ?? ''
      const sep = existing && !/\s$/.test(existing) ? ' ' : ''
      fill[i] = { ...cur, expr: `${existing}${sep}${token}` }
      return { ...prev, fill }
    })
  }

  const removeFill = (i: number) => {
    setDraft(prev => ({
      ...prev,
      fill: (prev.fill || []).filter((_, idx) => idx !== i),
    }))
  }

  const handleSave = () => {
    if (hasErrors) return
    const fill: ItemChangeFillMapping[] = (draft.fill || [])
      .map(f => {
        if (isExprFill(f)) {
          return { form_col: f.form_col.trim(), expr: (f.expr || '').trim() }
        }
        return { form_col: f.form_col.trim(), table_col: (f.table_col || '').trim() }
      })
      .filter(f => f.form_col && ((f.expr || '').trim() || (f.table_col || '').trim()))
    const cleaned: ItemChangeRule = {
      mode: 'rule',
      source_table: draft.source_table.trim(),
      match_mode: showAdvancedMatch ? 'advanced' : 'simple',
      fill,
    }
    // Source table / match are only meaningful with a lookup fill — drop
    // them entirely for a pure-expression rule so the runtime skips the query.
    const keepLookup = fill.some(f => (f.table_col || '').trim())
    if (!keepLookup) {
      cleaned.source_table = ''
    }
    if (keepLookup) {
      if (showAdvancedMatch) {
        cleaned.where_sql = (draft.where_sql || '').trim()
      } else {
        cleaned.match_table_col = (draft.match_table_col || '').trim()
      }
    }
    if (draft.on_no_match) cleaned.on_no_match = draft.on_no_match
    if (draft.on_multi_match) cleaned.on_multi_match = draft.on_multi_match
    // Conditional (un)protect entries have no editor UI yet — preserve any
    // agent-/hand-authored ones instead of silently dropping them on save.
    if (Array.isArray(draft.protect) && draft.protect.length) {
      cleaned.protect = draft.protect
    }
    onSave(cleaned)
    onClose()
  }

  // Form columns the user can choose to fill — for a per-column item_change,
  // exclude the trigger column to prevent a feedback loop (changing X writes
  // back to X, refiring change). A form-level event has no trigger, so every
  // column is a valid target.
  const fillCandidates = useMemo(
    () => formLevel ? formColumnNames : formColumnNames.filter(n => n !== triggerColumnName),
    [formColumnNames, triggerColumnName, formLevel],
  )

  const triggerLoopRows = formLevel ? [] : (draft.fill || [])
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => f.form_col === triggerColumnName)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={formLevel ? 'Row Event Fill Rule' : 'On-Change Fill Rule'}
      size="default"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={hasErrors}
            onClick={handleSave}
          >
            Save Rule
          </button>
        </div>
      }
    >
      <div>
        <div style={{ ...helpStyle, fontStyle: 'normal', marginTop: 0, marginBottom: 8 }}>
          {formLevel ? (
            <>When the row is loaded, fill values into fields on the screen — from an
            expression over the form/header, or looked up from a table.</>
          ) : (
            <>When <code style={{ color: 'var(--color-text)' }}>{triggerColumnName}</code> changes,
            look up a row in the chosen table and fill values into other fields on the screen.</>
          )}
        </div>

        {/* Source table */}
        <div style={sectionTitleStyle}>
          1. Pick the source table
          {!hasLookupFill && (
            <span style={{ fontWeight: 400, fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
              {' '}— optional (only for table fills)
            </span>
          )}
        </div>
        <label style={labelStyle}>Source Table</label>
        <input
          style={{
            ...inputStyle,
            border: errors.source_table ? '1px solid #ef4444' : inputStyle.border,
          }}
          value={draft.source_table}
          placeholder="e.g. PRODUCTS"
          list={tablesListId}
          autoComplete="off"
          onChange={e => update('source_table', e.target.value)}
        />
        <datalist id={tablesListId}>
          {tables.map(t => (
            <option key={t.name} value={t.name}>
              {t.description || ''}
            </option>
          ))}
        </datalist>
        {tablesError && (
          <div style={{ ...helpStyle, color: '#ef4444' }}>{tablesError}</div>
        )}
        {!tablesError && tables.length > 0 && (
          <div style={helpStyle}>
            {tables.length} project table{tables.length === 1 ? '' : 's'} suggested — start typing or pick one. You can also type a table name that isn&apos;t listed yet.
          </div>
        )}
        {errors.source_table && <div style={errorStyle}>{errors.source_table}</div>}

        {/* Shared datalist with this source-table's columns. */}
        <datalist id={columnsListId}>
          {sourceTableColumns.map(c => (
            <option key={c.column_name} value={c.column_name}>
              {c.data_type || ''}
            </option>
          ))}
        </datalist>

        {/* Match */}
        <div style={sectionTitleStyle}>
          2. Match by
          {formLevel && (
            <span style={{ fontWeight: 400, fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
              {' '}— only for table fills
            </span>
          )}
        </div>
        {(!showAdvancedMatch && !formLevel) ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                this column =
              </span>
              <input
                style={{
                  ...inputStyle, flex: 1,
                  border: errors.match ? '1px solid #ef4444' : inputStyle.border,
                }}
                value={draft.match_table_col || ''}
                placeholder="source table column (e.g. sku)"
                list={columnsListId}
                autoComplete="off"
                onChange={e => update('match_table_col', e.target.value)}
              />
            </div>
            <div style={helpStyle}>
              Looks up the row where the source table&apos;s chosen column equals
              the current value of <code style={{ color: 'var(--color-text)' }}>{triggerColumnName}</code>.
              {columnsLoading && ' Loading column suggestions…'}
              {columnsError && (
                <span style={{ color: '#ef4444' }}> {columnsError}</span>
              )}
            </div>
            <button
              type="button"
              style={advancedLinkStyle}
              onClick={() => setShowAdvancedMatch(true)}
            >
              Advanced: write a WHERE clause
            </button>
          </>
        ) : (
          <>
            <label style={labelStyle}>WHERE clause</label>
            <textarea
              style={{
                ...inputStyle, minHeight: 60, resize: 'vertical',
                fontFamily: 'monospace',
                border: errors.match ? '1px solid #ef4444' : inputStyle.border,
              }}
              value={draft.where_sql || ''}
              placeholder={formLevel
                ? `code = {header.type_code} AND active = true`
                : `sku = {form.${triggerColumnName}} AND active = true`}
              onChange={e => update('where_sql', e.target.value)}
            />
            <div style={helpStyle}>
              Use <code style={{ color: 'var(--color-text)' }}>{'{form.columnName}'}</code>
              {' '}(or <code style={{ color: 'var(--color-text)' }}>{'{header.columnName}'}</code>) to
              reference a value from the current screen. Values are bound as
              parameters, not interpolated.
            </div>
            {!formLevel && (
              <button
                type="button"
                style={advancedLinkStyle}
                onClick={() => setShowAdvancedMatch(false)}
              >
                ← Back to simple match
              </button>
            )}
          </>
        )}
        {errors.match && <div style={errorStyle}>{errors.match}</div>}

        {/* Fill mappings */}
        <div style={sectionTitleStyle}>3. Fill these fields</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(draft.fill || []).map((f, i) => {
            const expr = isExprFill(f)
            return (
              <div key={i} style={{
                display: 'flex', flexDirection: 'column', gap: 6,
                border: '1px solid var(--color-border)', borderRadius: 6,
                padding: '8px 8px 6px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <select
                    style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
                    value={f.form_col}
                    onChange={e => updateFill(i, { form_col: e.target.value })}
                  >
                    <option value="">— form column —</option>
                    {fillCandidates.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    {/* Keep already-selected value visible even if it's the trigger column. */}
                    {f.form_col && !fillCandidates.includes(f.form_col) && (
                      <option value={f.form_col}>{f.form_col}</option>
                    )}
                  </select>
                  <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8625rem' }}>←</span>
                  {/* Kind toggle: copy a table column vs evaluate an expression. */}
                  <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                    <button
                      type="button"
                      style={kindBtn(!expr)}
                      onClick={() => setFillKind(i, 'table')}
                    >
                      Table
                    </button>
                    <button
                      type="button"
                      style={{ ...kindBtn(expr), borderLeft: '1px solid var(--color-border)' }}
                      onClick={() => setFillKind(i, 'expr')}
                    >
                      Expression
                    </button>
                  </div>
                  <button
                    type="button"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--color-error, #cf222e)', padding: 2,
                    }}
                    title="Remove mapping"
                    onClick={() => removeFill(i)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {expr ? (
                  <>
                    <textarea
                      style={{
                        ...inputStyle, minHeight: 44, resize: 'vertical',
                        fontFamily: 'monospace',
                      }}
                      value={f.expr || ''}
                      placeholder={`{form.quantity} * {form.rate} - {header.discount}`}
                      onChange={e => updateFill(i, { expr: e.target.value })}
                    />
                    {(formColumnNames.length > 0 || headerColumnNames.length > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          insert:
                        </span>
                        {/* The trigger column IS a valid reference in an
                            expression (e.g. quantity * rate), so offer every
                            form column here — unlike the target dropdown. */}
                        {formColumnNames.map(n => (
                          <button key={`f-${n}`} type="button" style={tokenChipStyle}
                            onClick={() => appendToken(i, `{form.${n}}`)}
                            title={`Insert {form.${n}}`}
                          >{n}</button>
                        ))}
                        {headerColumnNames.map(n => (
                          <button key={`h-${n}`} type="button" style={{ ...tokenChipStyle, borderStyle: 'dashed' }}
                            onClick={() => appendToken(i, `{header.${n}}`)}
                            title={`Insert {header.${n}} (header form)`}
                          >header.{n}</button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    style={{ ...inputStyle }}
                    value={f.table_col || ''}
                    placeholder="table column"
                    list={columnsListId}
                    autoComplete="off"
                    onChange={e => updateFill(i, { table_col: e.target.value })}
                  />
                )}
              </div>
            )
          })}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={addFill}
              style={addBtnStyle}
            >
              <Plus size={12} /> Add table fill
            </button>
            <button
              type="button"
              onClick={addExprFill}
              style={addBtnStyle}
            >
              <Plus size={12} /> Add expression
            </button>
          </div>
          <div style={helpStyle}>
            Expressions use <code style={{ color: 'var(--color-text)' }}>+ − * / %</code>,
            parentheses and helpers <code style={{ color: 'var(--color-text)' }}>round, abs, min, max, iif(cond,a,b)</code>.
            Reference fields with a bare name (current form) or
            <code style={{ color: 'var(--color-text)' }}>{' {form.col}'}</code> /
            <code style={{ color: 'var(--color-text)' }}>{' {header.col}'}</code>.
            Expression fills run after table fills, so they can use freshly-filled columns.
          </div>
        </div>
        {errors.fill && <div style={errorStyle}>{errors.fill}</div>}
        {triggerLoopRows.length > 0 && (
          <div style={{ ...errorStyle, color: '#d97706' }}>
            Warning: a fill maps back to the trigger column
            (<code>{triggerColumnName}</code>). At runtime this write is skipped
            to avoid a feedback loop.
          </div>
        )}

        {/* Advanced options */}
        <button
          type="button"
          style={{ ...advancedLinkStyle, display: 'block', marginTop: 14 }}
          onClick={() => setShowAdvancedOptions(v => !v)}
        >
          {showAdvancedOptions ? '− Hide advanced options' : '+ Advanced options'}
        </button>
        {showAdvancedOptions && (
          <div style={{
            marginTop: 8, padding: 8,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)', borderRadius: 4,
          }}>
            <label style={labelStyle}>When no row matches</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={draft.on_no_match || ''}
              onChange={e =>
                update('on_no_match', (e.target.value || undefined) as ItemChangeRule['on_no_match'])
              }
            >
              <option value="">Default (clear filled fields)</option>
              <option value="clear">Clear filled fields</option>
              <option value="leave">Leave filled fields as-is</option>
            </select>
            <label style={labelStyle}>When multiple rows match</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={draft.on_multi_match || ''}
              onChange={e =>
                update('on_multi_match', (e.target.value || undefined) as ItemChangeRule['on_multi_match'])
              }
            >
              <option value="">Default (use the first row)</option>
              <option value="first">Use the first row</option>
              <option value="warn">Use first row and show a warning</option>
              <option value="open_lookup">Open the lookup picker to choose</option>
            </select>
          </div>
        )}
      </div>
    </Modal>
  )
}
